import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin, Confetti, Waves, ForkKnife, Coffee, Bicycle, Airplane, Boat, Storefront, Rainbow, ArrowRight,
} from "@phosphor-icons/react";
import LocalGuide from "@/components/site/LocalGuide";
import EventsTeaser from "@/components/site/EventsTeaser";

// Leaflet is heavy and only used here — lazy-load it so it stays out of the
// shared/homepage bundle. react-snap still prerenders the map into the static HTML.
const PropertyMap = lazy(() => import("@/components/site/PropertyMap"));
const MapPlaceholder = () => (
  <div className="w-full h-[420px] md:h-[520px] rounded-3xl border border-emerald/10 bg-emerald/5 animate-pulse" aria-hidden="true" />
);
import Seo, { SITE } from "@/components/site/Seo";
import { breadcrumbJsonLd } from "@/lib/schema";

const NB_HERO_ID = "photo-1594128740972-0f855f0efcc0";
const nbUnsplash = (w, q = 60) =>
  `https://images.unsplash.com/${NB_HERO_ID}?crop=entropy&fit=crop&auto=format&q=${q}&w=${w}`;
const HERO = nbUnsplash(1600);
const HERO_SRCSET = [640, 960, 1280, 1600, 2000].map((w) => `${nbUnsplash(w)} ${w}w`).join(", ");

const guides = [
  {
    tag: "01 — Nightlife",
    title: "Wilton Drive",
    image: "/wilton-pride-night.jpg",
    body: (
      <>
        {"The beating heart of Wilton Manors and one of the country's most welcoming LGBTQ+ scenes. Cocktail bars, dance floors, drag brunches, galleries and sidewalk cafés line the Drive — and every one of our stays is a short walk or bike ride away."}
        {" Browse our "}
        <Link to="/wilton-manors-vacation-rentals" data-testid="guide-link-wilton-rentals" className="text-emerald underline decoration-sunset/60 decoration-1 underline-offset-4 transition-colors duration-300 hover:text-sunset">Wilton Manors vacation rentals</Link>
        {" — every one is "}
        <Link to="/fort-lauderdale-pet-friendly-vacation-rentals" data-testid="guide-link-pet-friendly" className="text-emerald underline decoration-sunset/60 decoration-1 underline-offset-4 transition-colors duration-300 hover:text-sunset">pet-friendly</Link>
        {"."}
      </>
    ),
    highlights: [
      { icon: Rainbow, text: "LGBTQ+ owned & operated — all are welcome" },
    ],
    directory: {
      heading: "Walkable from Double Rainbow Suites — Wilton Manors",
      groups: [
        {
          title: "Food",
          icon: ForkKnife,
          items: [
            "Rosie's Bar & Grill — burgers, sandwiches & more",
            "Proof — tapas & shareables",
            "Ethos Greek Bistro — Greek",
            "Apt 9F — American, bar",
            "Gym Sports Bar — American, bar",
            "Tulio's Tacos & Tequila Bar — Mexican",
            "Union — American / European",
            "Tropics Grille — American, bar",
            "Inika — vegetarian",
            "Georgie's Alibi Monkey Bar — American, bar",
            "Stork's Bakery & Café — coffee & pastries",
            "Dairy Queen — ice cream",
            "Jazzberry — ice cream",
          ],
        },
        {
          title: "Nightlife, bars & clubs",
          icon: Confetti,
          items: [
            "Hunters Nightclub — gay nightclub & dancing",
            "No Manors — bar & lounge",
            "Drynk — bar & lounge",
            "LIT Bar",
            "The Eagle — leather bar",
            "The Pub",
          ],
        },
        {
          title: "Stores",
          icon: Storefront,
          items: ["Publix", "CVS"],
        },
      ],
    },
    driveDirectory: {
      heading: "A short drive, Uber or Lyft from Double Rainbow Suites",
      image: "/las-olas-night.jpg",
      groups: [
        {
          title: "Dining & entertainment",
          icon: ForkKnife,
          items: [
            "Las Olas — restaurants, bars & nightlife · 3 mi",
            "Fort Lauderdale Convention Center · 5.6 mi",
            "Broward Center for the Performing Arts · 3.9 mi",
            "Stonewall National Museum, Archives & Library",
            "Funky Buddha Brewery",
          ],
        },
      ],
    },
  },
  {
    tag: "02 — Sun & sand",
    title: "The Beaches",
    image: "/beach-ftl.jpg",
    enhance: true,
    body: (
      <>
        {"Fort Lauderdale's famous shoreline is just a 10-minute drive — or grab one of our free beach-cruiser bikes and coast over. Wide golden sand, warm water and palm-lined promenades await."}
        {" See our "}
        <Link to="/fort-lauderdale-vacation-rentals-near-beach" data-testid="guide-link-beach-rentals" className="text-emerald underline decoration-sunset/60 decoration-1 underline-offset-4 transition-colors duration-300 hover:text-sunset">vacation rentals near Fort Lauderdale Beach</Link>
        {" — or the home with a "}
        <Link to="/fort-lauderdale-vacation-rentals-with-pool" data-testid="guide-link-pool-rentals" className="text-emerald underline decoration-sunset/60 decoration-1 underline-offset-4 transition-colors duration-300 hover:text-sunset">private pool</Link>
        {"."}
      </>
    ),
    highlights: [
      { icon: Waves, text: "Fort Lauderdale Beach — 2.5 miles / ~10 min" },
      { icon: Waves, text: "Sebastian Street Beach & Las Olas by the Sea" },
      { icon: Bicycle, text: "Free beach-cruiser bikes at every property" },
    ],
    directory: {
      heading: "Beaches within easy reach",
      groups: [
        {
          title: "By distance",
          icon: Waves,
          items: [
            "Fort Lauderdale Beach · 2.5 mi",
            "Sebastian Beach · 2.8 mi",
            "Lauderdale-By-The-Sea · 4.1 mi",
            "Pompano Beach Pier · 19 mi",
            "Haulover Beach (clothing-optional) · 23 mi",
            "Miami South Beach · 28 mi · ~40 min drive",
            "Key West · 190 mi · ~3.5–4 hr drive",
          ],
        },
      ],
    },
  },
];

const distances = [
  { icon: Confetti, label: "Wilton Drive", value: "1–3 blocks" },
  { icon: Waves, label: "Fort Lauderdale Beach", value: "~10 min drive" },
  { icon: ForkKnife, label: "Las Olas Boulevard", value: "~10 min drive" },
  { icon: Airplane, label: "FLL Airport", value: "~15 min drive" },
  { icon: Boat, label: "Port Everglades cruise", value: "~6–7 miles" },
  { icon: Storefront, label: "Publix, Walgreens & CVS", value: "within 1 mile" },
];

export default function Neighbourhood() {
  return (
    <main>
      <Seo
        title="Neighborhood Guide | Wilton Manors & Fort Lauderdale — Double Rainbow Suites"
        description="Where to stay near Wilton Drive — your local guide to Wilton Manors and Fort Lauderdale: nightlife, beaches, dining, how far the beach is, getting around without a car, and the best time to visit. Curated by Double Rainbow Suites, the area's premier vacation rental host."
        canonical={`${SITE}/neighborhood`}
        jsonLd={breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Neighborhood Guide", path: "/neighborhood" },
        ])}
      />
      {/* Hero */}
      <section className="relative h-[62vh] min-h-[440px] overflow-hidden">
        <img src={HERO} srcSet={HERO_SRCSET} sizes="100vw" alt="Fort Lauderdale palms by the beach" fetchPriority="high" decoding="async" style={{ filter: "brightness(1.14) saturate(1.22) contrast(1.05)" }} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald/75 via-emerald/15 to-transparent" />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-14">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }} className="overline text-gold mb-5">
            The neighborhood
          </motion.p>
          <h1 className="font-display font-light text-sand text-5xl md:text-8xl leading-[0.9] tracking-tight max-w-4xl">
            <motion.span initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} className="block">
              Wilton Manors &<br />Fort Lauderdale.
            </motion.span>
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-[1000px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
        <p className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-emerald">
          A safe, walkable, wonderfully vibrant pocket of South Florida — where tropical calm meets the buzz of the Drive and the beach is only ever minutes away.
        </p>
      </section>

      {/* Guide sections */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-8 flex flex-col gap-20 md:gap-28">
        {guides.map((g, i) => (
          <motion.div
            key={g.title}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className={`grid ${g.image ? "lg:grid-cols-2" : "grid-cols-1"} gap-10 lg:gap-16 items-center ${g.image && i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
            data-testid={`guide-section-${i}`}
          >
            {g.image && (
              <div className="relative overflow-hidden rounded-3xl aspect-[4/3] group">
                <img src={g.image} alt={g.title} style={g.filter ? { filter: g.filter } : g.enhance ? { filter: "saturate(1.12) contrast(1.06) brightness(1.02)" } : undefined} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald/40 to-transparent" />
              </div>
            )}
            <div>
              <p className="overline text-sunset mb-4">{g.tag}</p>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight mb-5">{g.title}</h2>
              <p className="text-emerald-soft text-lg leading-relaxed font-light mb-8 max-w-xl">{g.body}</p>
              <ul className="flex flex-col gap-4">
                {g.highlights.map((h, j) => (
                  <li key={j} className="flex items-center gap-4 text-emerald">
                    <span className="w-11 h-11 rounded-full bg-sand-2 grid place-items-center shrink-0">
                      <h.icon size={20} className="text-sunset" />
                    </span>
                    {h.text}
                  </li>
                ))}
              </ul>
            </div>

            {g.directory && (
              <div className="lg:col-span-2 mt-2" data-testid="wilton-directory">
                <p className="overline text-sunset mb-6">{g.directory.heading}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                  {g.directory.groups.map((grp) => (
                    <div key={grp.title}>
                      <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-emerald/12">
                        <grp.icon size={18} weight="duotone" className="text-sunset" />
                        <h3 className="font-display text-2xl text-emerald">{grp.title}</h3>
                      </div>
                      <ul className="flex flex-col gap-2.5">
                        {grp.items.map((it) => (
                          <li key={it} className="flex gap-2.5 text-emerald-soft leading-snug">
                            <span className="text-sunset mt-1.5 shrink-0">•</span>
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {g.driveDirectory && (
              <div className="lg:col-span-2 mt-4 pt-10 border-t border-emerald/12" data-testid="drive-directory">
                {g.driveDirectory.image && (
                  <div className="relative overflow-hidden rounded-2xl aspect-[21/9] mb-8">
                    <img src={g.driveDirectory.image} alt="Las Olas at night" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-emerald/40 to-transparent" />
                  </div>
                )}
                <p className="overline text-sunset mb-6">{g.driveDirectory.heading}</p>
                <div className="grid sm:grid-cols-2 gap-8 md:gap-10">
                  {g.driveDirectory.groups.map((grp) => (
                    <div key={grp.title}>
                      <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-emerald/12">
                        <grp.icon size={18} weight="duotone" className="text-sunset" />
                        <h3 className="font-display text-2xl text-emerald">{grp.title}</h3>
                      </div>
                      <ul className="flex flex-col gap-2.5">
                        {grp.items.map((it) => (
                          <li key={it} className="flex gap-2.5 text-emerald-soft leading-snug">
                            <span className="text-sunset mt-1.5 shrink-0">•</span>
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </section>

      {/* Deeper host-written guides */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-8" data-testid="deeper-guides">
        <div className="grid md:grid-cols-2 gap-6">
          <Link to="/wilton-manors-guide" data-testid="deeper-guide-wilton-manors" className="group rounded-3xl border border-emerald/12 bg-sand-2 p-8 md:p-10 transition-colors duration-300 hover:border-sunset/40">
            <p className="overline text-sunset mb-3">The definitive local guide</p>
            <h2 className="font-display text-3xl md:text-4xl text-emerald leading-tight mb-3">Wilton Manors, by the people who host here.</h2>
            <p className="text-emerald-soft font-light mb-6">Best restaurants, gay bars, brunch, coffee, parking, walking distances and bike routes — straight from Bill &amp; Jonathan.</p>
            <span className="inline-flex items-center gap-2 text-emerald font-medium transition-colors duration-300 group-hover:text-sunset">
              Read the Wilton Manors guide <ArrowRight size={17} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </Link>
          <Link to="/fort-lauderdale-guide" data-testid="deeper-guide-fort-lauderdale" className="group rounded-3xl border border-emerald/12 bg-sand-2 p-8 md:p-10 transition-colors duration-300 hover:border-sunset/40">
            <p className="overline text-sunset mb-3">The definitive local guide</p>
            <h2 className="font-display text-3xl md:text-4xl text-emerald leading-tight mb-3">Fort Lauderdale, beyond the postcard.</h2>
            <p className="text-emerald-soft font-light mb-6">The best beaches, Las Olas, Riverwalk, Flagler Village, the cruise port, rainy-day plans and pet-friendly ideas.</p>
            <span className="inline-flex items-center gap-2 text-emerald font-medium transition-colors duration-300 group-hover:text-sunset">
              Read the Fort Lauderdale guide <ArrowRight size={17} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </Link>
        </div>
      </section>

      {/* Long-form authority guide */}
      <LocalGuide />

      {/* Getting around */}
      <section data-testid="distances-section" className="bg-emerald text-sand py-24 md:py-32 mt-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-12">
            <Bicycle size={26} className="text-gold" />
            <p className="overline text-gold">Getting around</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-sand/15 rounded-2xl overflow-hidden">
            {distances.map((d) => (
              <div key={d.label} className="bg-emerald p-8 flex items-start gap-4">
                <d.icon size={26} className="text-sunset shrink-0 mt-1" />
                <div>
                  <p className="text-sand text-lg">{d.label}</p>
                  <p className="text-sand/60 text-sm mt-1">{d.value}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-sand/60 mt-8 max-w-2xl font-light">
            Free parking and complimentary beach-cruiser bikes come with every stay — so you can leave the car and roll straight to the Drive or the sand.
            {" Sailing from Port Everglades? Our "}
            <Link to="/fort-lauderdale-vacation-rentals-near-cruise-port" data-testid="guide-link-cruise-rentals" className="text-sand underline decoration-gold/70 decoration-1 underline-offset-4 transition-colors duration-300 hover:text-gold">vacation rentals near the cruise port</Link>
            {" make embarkation day easy."}
          </p>
        </div>
      </section>

      {/* Map */}
      <section data-testid="map-section" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <MapPin size={22} weight="fill" className="text-sunset" />
            <p className="overline text-sunset">On the map</p>
          </div>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-2xl">
            See how close everything is.
          </h2>
        </div>
        <Suspense fallback={<MapPlaceholder />}>
          <PropertyMap />
        </Suspense>
      </section>

      {/* WHAT'S ON */}
      <EventsTeaser theme="dark" />

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="relative overflow-hidden rounded-3xl bg-sand-2 border border-emerald/10 p-10 md:p-20 text-center">
          <p className="overline text-sunset mb-5">Make it home base</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight mb-8 max-w-3xl mx-auto">
            Stay in the middle of it all.
          </h2>
          <Link to="/properties" data-testid="guide-cta-btn" className="group inline-flex items-center gap-2 rounded-full bg-sunset px-8 py-4 text-sand font-medium transition-colors duration-300 hover:bg-emerald">
            Explore our stays <ArrowRight size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </main>
  );
}
